import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const OrderModal = ({ order, onClose, onUpdate, isEditMode = false, accountNumber, onBackToAccount, user }) => {
    const [activeTab, setActiveTab] = useState('info');
    const [editMode, setEditMode] = useState(isEditMode);
    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [statementsLoading, setStatementsLoading] = useState(false);
    const [statements, setStatements] = useState([]);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [orderDetails, setOrderDetails] = useState([]);
    const [detailsMessage, setDetailsMessage] = useState('');
    
    // Новые состояния для автозаполнения
    const [filteredAccounts, setFilteredAccounts] = useState([]);
    const [showAccountSuggestions, setShowAccountSuggestions] = useState(false);
    const [searchingAccounts, setSearchingAccounts] = useState(false);
    const [owners, setOwners] = useState([]);
    const [filteredOwners, setFilteredOwners] = useState([]);
    const [showOwnerSuggestions, setShowOwnerSuggestions] = useState(false);
    const [selectedAccountNumber, setSelectedAccountNumber] = useState(null);

    // Функция для форматирования даты в формат input[type="date"]
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    // Загрузка собственников при изменении выбранного номера счета
    useEffect(() => {
        const loadOwners = async () => {
            if (selectedAccountNumber) {
                try {
                    const response = await api.getOwnersByAccountNumber(selectedAccountNumber);
                    if (response.data && response.data.owners && Array.isArray(response.data.owners)) {
                        const ownersList = response.data.owners;
                        setOwners(ownersList);
                        setFilteredOwners(ownersList);
                        // Автоматически заполняем первым собственником
                        if (ownersList.length > 0) {
                            const firstOwner = ownersList[0];
                            setFormData(prev => {
                                if (!prev) return prev;
                                return {
                                    ...prev,
                                    personName: firstOwner.name || '',
                                    personINN: firstOwner.inn || '',
                                    personID: firstOwner.id || ''
                                };
                            });
                        }
                    } else {
                        setOwners([]);
                        setFilteredOwners([]);
                    }
                } catch (error) {
                    console.error('Error loading owners:', error);
                    setOwners([]);
                    setFilteredOwners([]);
                }
            } else {
                setOwners([]);
                setFilteredOwners([]);
            }
        };

        loadOwners();
    }, [selectedAccountNumber]);

    useEffect(() => {
        if (order) {
            setFormData({
                // Основная информация
                id: order?.id || '',
                accountNumber: order?.accountNumber || '',
                executorName: order?.executorName || '',
                agentINN: order?.agentINN || '',
                documentType: order?.documentType || '',
                outNumber: order?.outNumber || '',
                documentDate: order?.documentDate ? formatDateForInput(order.documentDate) : '',

                // Период взыскания
                collectionPeriodFrom: order?.collectionPeriodFrom ? formatDateForInput(order.collectionPeriodFrom) : '',
                collectionPeriodTo: order?.collectionPeriodTo ? formatDateForInput(order.collectionPeriodTo) : '',

                // Суммы иска
                amountClaimFee: order?.amountClaimFee || '',
                amountClaimPenalties: order?.amountClaimPenalties || '',
                amountClaimToll: order?.amountClaimToll || '',
                amountUnusedToll: order?.amountUnusedToll || '',

                // Судебная информация
                nameCourt: order?.nameCourt || '',
                dateAcceptance: order?.dateAcceptance ? formatDateForInput(order.dateAcceptance) : '',
                caseNumber: order?.caseNumber || '',
                reviewResult: order?.reviewResult || '',

                // Взысканные суммы
                amountRecoveredFee: order?.amountRecoveredFee || '',
                amountRecoveredPenalties: order?.amountRecoveredPenalties || '',
                amountRecoveredToll: order?.amountRecoveredToll || '',
                amountVoluntaryExecution: order?.amountVoluntaryExecution || '',

                // Даты решения
                decisionDate: order?.decisionDate ? formatDateForInput(order.decisionDate) : '',
                effectiveDate: order?.effectiveDate ? formatDateForInput(order.effectiveDate) : '',

                // Апелляция и выдача
                appeal: order?.appeal || '',
                dateIssue: order?.dateIssue ? formatDateForInput(order.dateIssue) : '',
                sourceDocumentNumber: order?.sourceDocumentNumber || '',

                // Даты получения и исполнения
                dateReceipt: order?.dateReceipt ? formatDateForInput(order.dateReceipt) : '',
                dateExecution: order?.dateExecution ? formatDateForInput(order.dateExecution) : '',

                // Исполнительное производство
                institutionCollection: order?.institutionCollection || '',
                stage: order?.stage || '',
                amountEnforcementFee: order?.amountEnforcementFee || '',
                amountEnforcementPenalties: order?.amountEnforcementPenalties || '',
                amountEnforcementToll: order?.amountEnforcementToll || '',

                // Исполнительные документы
                amountExecDocFee: order?.amountExecDocFee || '',
                amountExecDocPenalties: order?.amountExecDocPenalties || '',
                amountExecDocToll: order?.amountExecDocToll || '',
                amountRefundedToll: order?.amountRefundedToll || '',

                // Примечания
                note: order?.note || '',
                comments: order?.comments || '',

                // Person и Address
                personID: order?.person?.id || '',
                personName: order?.person?.name || '',
                personINN: order?.person?.inn || '',
                addressCity: order?.address?.city || '',
                addressStreet: order?.address?.street || '',
                addressHome: order?.address?.home || '',
                addressApartment: order?.address?.apartment || ''
            });
        } else {
            // Пустая форма для создания нового приказа
            setFormData({
                accountNumber: accountNumber || '',
                executorName: '',
                agentINN: '',
                documentType: '',
                outNumber: '',
                documentDate: '',
                collectionPeriodFrom: '',
                collectionPeriodTo: '',
                amountClaimFee: '',
                amountClaimPenalties: '',
                amountClaimToll: '',
                amountUnusedToll: '',
                nameCourt: '',
                dateAcceptance: '',
                caseNumber: '',
                reviewResult: '',
                amountRecoveredFee: '',
                amountRecoveredPenalties: '',
                amountRecoveredToll: '',
                amountVoluntaryExecution: '',
                decisionDate: '',
                effectiveDate: '',
                appeal: '',
                dateIssue: '',
                sourceDocumentNumber: '',
                dateReceipt: '',
                dateExecution: '',
                institutionCollection: '',
                stage: '',
                amountEnforcementFee: '',
                amountEnforcementPenalties: '',
                amountEnforcementToll: '',
                amountExecDocFee: '',
                amountExecDocPenalties: '',
                amountExecDocToll: '',
                amountRefundedToll: '',
                note: '',
                comments: '',
                personName: '',
                personINN: '',
                addressCity: '',
                addressStreet: '',
                addressHome: '',
                addressApartment: ''
            });
        }
    }, [order, accountNumber]);

    useEffect(() => {
        if (activeTab === 'statements' && order) {
            loadStatements();
        }
        if (activeTab === 'details' && order) {
            loadOrderDetails();
        }
    }, [activeTab, order]);

    const loadOrderDetails = async () => {
        if (!order) return;
        setDetailsLoading(true);
        setDetailsMessage('');
        try {
            const response = await api.getDetailsByOrder(order.id);
            setOrderDetails(response.data || []);
        } catch (error) {
            console.error('Error loading order details:', error);
            setDetailsMessage('Ошибка загрузки детализации');
        } finally {
            setDetailsLoading(false);
        }
    };
    
    const loadStatements = async () => {
        if (!order) return;

        setStatementsLoading(true);
        try {
            const response = await api.getStatementsByOrderId(order.id);
            setStatements(response.data.data || []);
        } catch (error) {
            console.error('Error loading Statements:', error);
            setMessage('Ошибка загрузки выписок');
        } finally {
            setStatementsLoading(false);
        }
    };

    // Удаление выписки
    const handleDeleteStatement = async (e, statementId) => {
        e.stopPropagation();
        if (!window.confirm('Вы уверены, что хотите удалить эту выписку?')) return;

        try {
            await api.deleteStatementSaved(statementId);
            setMessage('Выписка удалена успешно');
            loadStatements(); // Перезагружаем список выписок
        } catch (error) {
            console.error('Error deleting statement:', error);
            setMessage('Ошибка при удалении выписки');
        }
    };

    // Функция для рендеринга заголовка с хлебными крошками
    const renderHeaderTitle = () => {
        if (editMode) {
            return order?.id ? 'Редактирование приказа' : 'Создание приказа';
        }

        // Режим просмотра - показываем хлебные крошки
        if (accountNumber) {
            return (
                <div className="breadcrumb-header">
                    <span
                        className="breadcrumb-link"
                        onClick={handleBreadcrumbClick}
                        title="Перейти к лицевому счету"
                    >
                        Лицевой счет № {accountNumber}
                    </span>
                    <span className="breadcrumb-separator"> → </span>
                    <span>Приказ № {order?.caseNumber || '...'}</span>
                </div>
            );
        }

        // Если номер счета не передан, показываем простой заголовок
        return `Приказ № ${order?.caseNumber || '...'}`;
    };

    // Обработчик клика по хлебной крошке
    const handleBreadcrumbClick = () => {
        if (accountNumber) {
            onClose();
            if (onBackToAccount) {
                onBackToAccount();
            }
        }
    };

    // Обработчик изменения номера счета с автозаполнением
    const handleAccountNumberChange = async (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            accountNumber: value,
            personName: '', // Сбрасываем выбранного собственника
            personINN: '',
            personID: ''
        }));

        if (value && value.length > 0) {
            setSearchingAccounts(true);
            try {
                const response = await api.searchAccountNumbers(value);
                if (response.data && Array.isArray(response.data)) {
                    setFilteredAccounts(response.data);
                    setShowAccountSuggestions(response.data.length > 0);
                } else {
                    setFilteredAccounts([]);
                    setShowAccountSuggestions(false);
                }
            } catch (error) {
                console.error('Error searching account numbers:', error);
                setFilteredAccounts([]);
                setShowAccountSuggestions(false);
            } finally {
                setSearchingAccounts(false);
            }
        } else {
            setFilteredAccounts([]);
            setShowAccountSuggestions(false);
        }
    };

    // Выбор номера счета из подсказок
    const handleSelectAccount = (accountNum) => {
        setFormData(prev => ({
            ...prev,
            accountNumber: accountNum,
            personName: '',
            personINN: '',
            personID: ''
        }));
        setShowAccountSuggestions(false);
        // Устанавливаем выбранный номер счёта - useEffect загрузит собственников
        setSelectedAccountNumber(accountNum);
    };

    // Обработчик изменения ФИО собственника с автозаполнением
    const handleOwnerNameChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({
            ...prev,
            personName: value
        }));

        if (value && owners.length > 0) {
            const filtered = owners.filter(owner =>
                owner.name.toLowerCase().includes(value.toLowerCase())
            );
            setFilteredOwners(filtered);
            setShowOwnerSuggestions(true);
        } else {
            setFilteredOwners(owners);
            setShowOwnerSuggestions(false);
        }
    };

    // Выбор собственника из подсказок
    const handleSelectOwner = (owner) => {
        setFormData(prev => ({
            ...prev,
            personName: owner.name,
            personINN: owner.inn || '',
            personID: owner.id || ''
        }));
        setShowOwnerSuggestions(false);
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? (value === '' ? '' : Number(value)) : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData) return;

        // Валидация обязательных полей
        if (!formData.accountNumber) {
            alert('Поле "Номер счета" обязательно для заполнения');
            return;
        }

        if (!formData.personName) {
            alert('Поле "ФИО собственника" обязательно для заполнения');
            return;
        }

        setLoading(true);
        try {
            // Функция для преобразования дат
            const parseDate = (dateStr) => {
                if (!dateStr) return null;
                return new Date(dateStr).toISOString();
            };

            // Подготовка данных для отправки
            const processedData = {
                id: formData.id ? parseInt(formData.id) : undefined,
                accountNumber: parseInt(formData.accountNumber),
                executorName: formData.executorName,
                agentINN: formData.agentINN,
                documentType: formData.documentType,
                outNumber: formData.outNumber,
                documentDate: parseDate(formData.documentDate),
                collectionPeriodFrom: parseDate(formData.collectionPeriodFrom),
                collectionPeriodTo: parseDate(formData.collectionPeriodTo),
                amountClaimFee: formData.amountClaimFee === '' ? 0 : Number(formData.amountClaimFee),
                amountClaimPenalties: formData.amountClaimPenalties === '' ? 0 : Number(formData.amountClaimPenalties),
                amountClaimToll: formData.amountClaimToll === '' ? 0 : Number(formData.amountClaimToll),
                amountUnusedToll: formData.amountUnusedToll === '' ? 0 : Number(formData.amountUnusedToll),
                nameCourt: formData.nameCourt,
                dateAcceptance: parseDate(formData.dateAcceptance),
                caseNumber: formData.caseNumber,
                reviewResult: formData.reviewResult,
                amountRecoveredFee: formData.amountRecoveredFee === '' ? 0 : Number(formData.amountRecoveredFee),
                amountRecoveredPenalties: formData.amountRecoveredPenalties === '' ? 0 : Number(formData.amountRecoveredPenalties),
                amountRecoveredToll: formData.amountRecoveredToll === '' ? 0 : Number(formData.amountRecoveredToll),
                amountVoluntaryExecution: formData.amountVoluntaryExecution === '' ? 0 : Number(formData.amountVoluntaryExecution),
                decisionDate: parseDate(formData.decisionDate),
                effectiveDate: parseDate(formData.effectiveDate),
                appeal: formData.appeal,
                dateIssue: parseDate(formData.dateIssue),
                sourceDocumentNumber: formData.sourceDocumentNumber,
                dateReceipt: parseDate(formData.dateReceipt),
                dateExecution: parseDate(formData.dateExecution),
                institutionCollection: formData.institutionCollection,
                amountEnforcementFee: formData.amountEnforcementFee === '' ? 0 : Number(formData.amountEnforcementFee),
                amountEnforcementPenalties: formData.amountEnforcementPenalties === '' ? 0 : Number(formData.amountEnforcementPenalties),
                amountEnforcementToll: formData.amountEnforcementToll === '' ? 0 : Number(formData.amountEnforcementToll),
                stage: formData.stage,
                amountExecDocFee: formData.amountExecDocFee === '' ? 0 : Number(formData.amountExecDocFee),
                amountExecDocPenalties: formData.amountExecDocPenalties === '' ? 0 : Number(formData.amountExecDocPenalties),
                amountExecDocToll: formData.amountExecDocToll === '' ? 0 : Number(formData.amountExecDocToll),
                amountRefundedToll: formData.amountRefundedToll === '' ? 0 : Number(formData.amountRefundedToll),
                note: formData.note,
                comments: formData.comments,
                // Объекты Person и Address
                person: formData.personName ? {
                    id: formData.personID ? parseInt(formData.personID) : undefined,
                    name: formData.personName,
                    inn: formData.personINN || ""
                } : null
            };
            // Явно устанавливаем null для пустых объектов
            if (processedData.address === null) {
                processedData.address = null;
            }
            
            // Удаляем поля с null/undefined значениями (кроме числовых id)
            Object.keys(processedData).forEach(key => {
                if (key === 'id') return; // id должен быть числом или отсутствовать
                if (processedData[key] === null || processedData[key] === undefined) {
                    processedData[key] = "";
                }
            });
            // Удаляем id если он undefined (создание нового приказа)
            if (processedData.id === undefined) {
                delete processedData.id;
            }
            // Удаляем id из person если он undefined
            if (processedData.person && processedData.person.id === undefined) {
                delete processedData.person.id;
            }

            if (order && order.id) {
                await api.updateOrder(order.id, processedData);
                setMessage('Приказ обновлен успешно');
            } else {
                await api.createOrder(processedData);
                setMessage('Приказ создан успешно');
            }

            if (onUpdate) {
                onUpdate();
            }

            // Выходим из режима редактирования после успешного сохранения
            setEditMode(false);

        } catch (error) {
            setMessage('Ошибка сохранения приказа');
            console.error('Save order error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!order?.id) return;

        if (!window.confirm('Вы уверены, что хотите удалить этот приказ?')) {
            return;
        }

        try {
            await api.deleteOrder(order.id);
            setMessage('Приказ удален успешно');

            if (onUpdate) {
                onUpdate();
            }

            setTimeout(() => {
                onClose();
            }, 1000);

        } catch (error) {
            setMessage('Ошибка удаления приказа');
            console.error('Delete order error:', error);
        }
    };

    // Функции форматирования для отображения
    const formatCurrency = (value) => {
        if (value === null || value === undefined || value === '') {
            return '-';
        }
        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(Number(value));
    };

    const formatDateToRussian = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    };

    if (!formData) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>{renderHeaderTitle()}</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <div className="modal-tabs">
                    <div className="tabs-header">
                        <button
                            className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
                            onClick={() => setActiveTab('info')}
                        >
                            Инфо
                        </button>
                        <button
                            className={`tab-button ${activeTab === 'statements' ? 'active' : ''}`}
                            onClick={() => setActiveTab('statements')}
                        >
                            Выписки
                        </button>
                        {order && (
                            <button
                                className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
                                onClick={() => setActiveTab('details')}
                            >
                                Детализация
                            </button>
                        )}
                    </div>

                    <div className="tab-content">
                        {activeTab === 'info' && (
                            <div className="info-tab">
                                {message && (
                                    <div className={`message ${message.includes('Ошибка') ? 'error' : 'success'}`}>
                                        {message}
                                    </div>
                                )}

                                <div className="tab-actions">
                                    {!editMode ? (
                                        <>
                                            {user && (user.role === 'admin' || user.role === 'lawyer') && (
                                                <>
                                                    <button
                                                        onClick={() => setEditMode(true)}
                                                        className="btn-primary"
                                                    >
                                                        Редактировать
                                                    </button>
                                                    <button
                                                        onClick={handleDelete}
                                                        className="btn-delete-modal"
                                                    >
                                                        Удалить
                                                    </button>
                                                </>
                                            )}
                                        </>
                                    ) : (
                                        <div className="edit-actions">
                                            <button
                                                onClick={handleSubmit}
                                                disabled={loading}
                                                className="btn-primary"
                                            >
                                                {loading ? 'Сохранение...' : 'Сохранить'}
                                            </button>
                                            <button
                                                onClick={() => {
                                                    // Если это новый приказ (нет order) - просто закрываем
                                                    if (!order) {
                                                        onClose();
                                                        return;
                                                    }
                                                    setEditMode(false);
                                                    // Сброс формы к исходным данным
                                                    if (order) {
                                                        setFormData({
                                                            id: order?.id || '',
                                                            accountNumber: order?.accountNumber || '',
                                                            executorName: order?.executorName || '',
                                                            agentINN: order?.agentINN || '',
                                                            documentType: order?.documentType || '',
                                                            outNumber: order?.outNumber || '',
                                                            documentDate: order?.documentDate ? formatDateForInput(order.documentDate) : '',
                                                            collectionPeriodFrom: order?.collectionPeriodFrom ? formatDateForInput(order.collectionPeriodFrom) : '',
                                                            collectionPeriodTo: order?.collectionPeriodTo ? formatDateForInput(order.collectionPeriodTo) : '',
                                                            amountClaimFee: order?.amountClaimFee || '',
                                                            amountClaimPenalties: order?.amountClaimPenalties || '',
                                                            amountClaimToll: order?.amountClaimToll || '',
                                                            amountUnusedToll: order?.amountUnusedToll || '',
                                                            nameCourt: order?.nameCourt || '',
                                                            dateAcceptance: order?.dateAcceptance ? formatDateForInput(order.dateAcceptance) : '',
                                                            caseNumber: order?.caseNumber || '',
                                                            reviewResult: order?.reviewResult || '',
                                                            amountRecoveredFee: order?.amountRecoveredFee || '',
                                                            amountRecoveredPenalties: order?.amountRecoveredPenalties || '',
                                                            amountRecoveredToll: order?.amountRecoveredToll || '',
                                                            amountVoluntaryExecution: order?.amountVoluntaryExecution || '',
                                                            decisionDate: order?.decisionDate ? formatDateForInput(order.decisionDate) : '',
                                                            effectiveDate: order?.effectiveDate ? formatDateForInput(order.effectiveDate) : '',
                                                            appeal: order?.appeal || '',
                                                            dateIssue: order?.dateIssue ? formatDateForInput(order.dateIssue) : '',
                                                            sourceDocumentNumber: order?.sourceDocumentNumber || '',
                                                            dateReceipt: order?.dateReceipt ? formatDateForInput(order.dateReceipt) : '',
                                                            dateExecution: order?.dateExecution ? formatDateForInput(order.dateExecution) : '',
                                                            institutionCollection: order?.institutionCollection || '',
                                                            stage: order?.stage || '',
                                                            amountEnforcementFee: order?.amountEnforcementFee || '',
                                                            amountEnforcementPenalties: order?.amountEnforcementPenalties || '',
                                                            amountEnforcementToll: order?.amountEnforcementToll || '',
                                                            amountExecDocFee: order?.amountExecDocFee || '',
                                                            amountExecDocPenalties: order?.amountExecDocPenalties || '',
                                                            amountExecDocToll: order?.amountExecDocToll || '',
                                                            amountRefundedToll: order?.amountRefundedToll || '',
                                                            note: order?.note || '',
                                                            comments: order?.comments || '',
                                                            personID: order?.person?.id || '',
                                                            personName: order?.person?.name || '',
                                                            personINN: order?.person?.inn || '',
                                                            addressCity: order?.address?.city || '',
                                                            addressStreet: order?.address?.street || '',
                                                            addressHome: order?.address?.home || '',
                                                            addressApartment: order?.address?.apartment || ''
                                                        });
                                                    } else {
                                                        // Для нового приказа - закрываем модальное окно
                                                        onClose();
                                                        return;
                                                    }
                                                    setSelectedAccountNumber(null);
                                                    setOwners([]);
                                                    setFilteredOwners([]);
                                                }}
                                                className="btn-secondary"
                                            >
                                                Отмена
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {editMode ? (
                                    <form onSubmit={handleSubmit} className="order-form">
                                        <div className="form-sections">
                                            {/* Системная информация */}
                                            {order?.id && (
                                                <div className="form-section">
                                                    <h4>Системная информация</h4>
                                                    <div className="form-row">
                                                        <div className="form-group">
                                                            <label>ID приказа:</label>
                                                            <input
                                                                type="text"
                                                                value={order.id}
                                                                disabled
                                                                style={{
                                                                    backgroundColor: '#f8f9fa',
                                                                    color: '#6c757d',
                                                                    cursor: 'not-allowed'
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Основная информация */}
                                            <div className="form-section">
                                                <h4>Основная информация</h4>
                                                <div className="form-row">
                                                    <div className="form-group" style={{ position: 'relative' }}>
                                                        <label>Номер счета *:</label>
                                                        <input
                                                            type="text"
                                                            name="accountNumber"
                                                            value={formData?.accountNumber || ''}
                                                            onChange={handleAccountNumberChange}
                                                            onFocus={() => {
                                                                if (formData && formData.accountNumber && filteredAccounts.length > 0) {
                                                                    setShowAccountSuggestions(true);
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                setTimeout(() => setShowAccountSuggestions(false), 200);
                                                            }}
                                                            placeholder="Введите номер счета"
                                                            required
                                                            autoComplete="off"
                                                        />
                                                        {searchingAccounts && (
                                                            <div className="autocomplete-suggestions">
                                                                <div className="autocomplete-item" style={{ textAlign: 'center', color: '#666' }}>
                                                                    Поиск...
                                                                </div>
                                                            </div>
                                                        )}
                                                        {!searchingAccounts && showAccountSuggestions && filteredAccounts.length > 0 && (
                                                            <div className="autocomplete-suggestions">
                                                                {filteredAccounts.slice(0, 10).map((acc) => (
                                                                    <div
                                                                        key={acc.number}
                                                                        className="autocomplete-item"
                                                                        onClick={() => handleSelectAccount(acc.number)}
                                                                    >
                                                                        {acc.number}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {!searchingAccounts && formData && formData.accountNumber && filteredAccounts.length === 0 && showAccountSuggestions && (
                                                            <div className="autocomplete-suggestions">
                                                                <div className="autocomplete-item" style={{ textAlign: 'center', color: '#999' }}>
                                                                    Счета не найдены
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Информация о собственнике - перенесена вверх */}
                                            <div className="form-section">
                                                <h4>Информация о собственнике</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>ФИО собственника *:</label>
                                                        {owners.length > 1 ? (
                                                            <select
                                                                name="personName"
                                                                value={formData.personName}
                                                                onChange={(e) => {
                                                                    const selectedOwner = owners.find(owner => owner.name === e.target.value);
                                                                    if (selectedOwner) {
                                                                        handleSelectOwner(selectedOwner);
                                                                    }
                                                                }}
                                                                required
                                                                disabled={!formData || !formData.accountNumber}
                                                            >
                                                                {owners.map((owner) => (
                                                                    <option key={owner.id} value={owner.name}>
                                                                        {owner.name}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        ) : (
                                                            <input
                                                                type="text"
                                                                name="personName"
                                                                value={formData.personName}
                                                                onChange={handleChange}
                                                                placeholder="ФИО собственника"
                                                                required
                                                                disabled={!formData || !formData.accountNumber}
                                                                readOnly={owners.length === 1}
                                                            />
                                                        )}
                                                    </div>
                                                    <div className="form-group">
                                                        <label>ИНН собственника:</label>
                                                        <input
                                                            type="text"
                                                            name="personINN"
                                                            value={formData.personINN}
                                                            onChange={handleChange}
                                                            placeholder="Введите ИНН собственника"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Дополнительная основная информация */}
                                            <div className="form-section">
                                                <h4>Дополнительная информация</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Исполнитель:</label>
                                                        <input
                                                            type="text"
                                                            name="executorName"
                                                            value={formData.executorName}
                                                            onChange={handleChange}
                                                            placeholder="Введите исполнителя"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>ИНН агента:</label>
                                                        <input
                                                            type="text"
                                                            name="agentINN"
                                                            value={formData.agentINN}
                                                            onChange={handleChange}
                                                            placeholder="Введите ИНН агента"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Тип документа:</label>
                                                        <input
                                                            type="text"
                                                            name="documentType"
                                                            value={formData.documentType}
                                                            onChange={handleChange}
                                                            placeholder="Введите тип документа"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Исходящий номер:</label>
                                                        <input
                                                            type="text"
                                                            name="outNumber"
                                                            value={formData.outNumber}
                                                            onChange={handleChange}
                                                            placeholder="Введите исходящий номер"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Исходящий номер:</label>
                                                        <input
                                                            type="text"
                                                            name="outNumber"
                                                            value={formData.outNumber}
                                                            onChange={handleChange}
                                                            placeholder="Введите исходящий номер"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Дата документа:</label>
                                                        <input
                                                            type="date"
                                                            name="documentDate"
                                                            value={formData.documentDate}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Период взыскания */}
                                            <div className="form-section">
                                                <h4>Период взыскания</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Период взыскания с:</label>
                                                        <input
                                                            type="date"
                                                            name="collectionPeriodFrom"
                                                            value={formData.collectionPeriodFrom}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Период взыскания по:</label>
                                                        <input
                                                            type="date"
                                                            name="collectionPeriodTo"
                                                            value={formData.collectionPeriodTo}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Финансовые данные - Иск */}
                                            <div className="form-section">
                                                <h4>Суммы иска</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Сумма иска (взнос):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountClaimFee"
                                                            value={Number(formData.amountClaimFee).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Сумма иска (пени):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountClaimPenalties"
                                                            value={Number(formData.amountClaimPenalties).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Сумма иска (пошлина):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountClaimToll"
                                                            value={Number(formData.amountClaimToll).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Неиспользованная пошлина:</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountUnusedToll"
                                                            value={Number(formData.amountUnusedToll).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Судебная информация */}
                                            <div className="form-section">
                                                <h4>Судебная информация</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Суд:</label>
                                                        <input
                                                            type="text"
                                                            name="nameCourt"
                                                            value={formData.nameCourt}
                                                            onChange={handleChange}
                                                            placeholder="Введите название суда"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Дата принятия:</label>
                                                        <input
                                                            type="date"
                                                            name="dateAcceptance"
                                                            value={formData.dateAcceptance}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Номер дела:</label>
                                                        <input
                                                            type="text"
                                                            name="caseNumber"
                                                            value={formData.caseNumber}
                                                            onChange={handleChange}
                                                            placeholder="Введите номер дела"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Результат рассмотрения:</label>
                                                        <input
                                                            type="text"
                                                            name="reviewResult"
                                                            value={formData.reviewResult}
                                                            onChange={handleChange}
                                                            placeholder="Введите результат"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Финансовые данные - Взыскано */}
                                            <div className="form-section">
                                                <h4>Взысканные суммы</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Взыскано (взнос):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountRecoveredFee"
                                                            value={Number(formData.amountRecoveredFee).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Взыскано (пени):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountRecoveredPenalties"
                                                            value={Number(formData.amountRecoveredPenalties).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Взыскано (пошлина):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountRecoveredToll"
                                                            value={Number(formData.amountRecoveredToll).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Добровольное исполнение:</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountVoluntaryExecution"
                                                            value={Number(formData.amountVoluntaryExecution).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Даты решения */}
                                            <div className="form-section">
                                                <h4>Даты решения</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Дата решения:</label>
                                                        <input
                                                            type="date"
                                                            name="decisionDate"
                                                            value={formData.decisionDate}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Дата вступления:</label>
                                                        <input
                                                            type="date"
                                                            name="effectiveDate"
                                                            value={formData.effectiveDate}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Информация об апелляции */}
                                            <div className="form-section">
                                                <h4>Апелляция и выдача</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Апелляция:</label>
                                                        <input
                                                            type="text"
                                                            name="appeal"
                                                            value={formData.appeal}
                                                            onChange={handleChange}
                                                            placeholder="Введите информацию об апелляции"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Дата выдачи:</label>
                                                        <input
                                                            type="date"
                                                            name="dateIssue"
                                                            value={formData.dateIssue}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Исходный номер документа:</label>
                                                        <input
                                                            type="text"
                                                            name="sourceDocumentNumber"
                                                            value={formData.sourceDocumentNumber}
                                                            onChange={handleChange}
                                                            placeholder="Введите исходный номер"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Даты получения и исполнения */}
                                            <div className="form-section">
                                                <h4>Даты получения и исполнения</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Дата получения:</label>
                                                        <input
                                                            type="date"
                                                            name="dateReceipt"
                                                            value={formData.dateReceipt}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Дата исполнения:</label>
                                                        <input
                                                            type="date"
                                                            name="dateExecution"
                                                            value={formData.dateExecution}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Исполнительное производство */}
                                            <div className="form-section">
                                                <h4>Исполнительное производство</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Учреждение взыскания:</label>
                                                        <input
                                                            type="text"
                                                            name="institutionCollection"
                                                            value={formData.institutionCollection}
                                                            onChange={handleChange}
                                                            placeholder="Введите учреждение"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Стадия:</label>
                                                        <input
                                                            type="text"
                                                            name="stage"
                                                            value={formData.stage}
                                                            onChange={handleChange}
                                                            placeholder="Введите стадию"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Исполнительный сбор (взнос):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountEnforcementFee"
                                                            value={Number(formData.amountEnforcementFee).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Исполнительный сбор (пени):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountEnforcementPenalties"
                                                            value={Number(formData.amountEnforcementPenalties).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Исполнительный сбор (пошлина):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountEnforcementToll"
                                                            value={Number(formData.amountEnforcementToll).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Исполнительные документы */}
                                            <div className="form-section">
                                                <h4>Исполнительные документы</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Испол. док. (взнос):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountExecDocFee"
                                                            value={Number(formData.amountExecDocFee).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Испол. док. (пени):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountExecDocPenalties"
                                                            value={Number(formData.amountExecDocPenalties).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Испол. док. (пошлина):</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountExecDocToll"
                                                            value={Number(formData.amountExecDocToll).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Возвращенная пошлина:</label>
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            name="amountRefundedToll"
                                                            value={Number(formData.amountRefundedToll).toFixed(2)}
                                                            onChange={handleChange}
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Адрес */}
                                            <div className="form-section">
                                                <h4>Адрес</h4>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Город:</label>
                                                        <input
                                                            type="text"
                                                            name="addressCity"
                                                            value={formData.addressCity}
                                                            onChange={handleChange}
                                                            placeholder="Введите город"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Улица:</label>
                                                        <input
                                                            type="text"
                                                            name="addressStreet"
                                                            value={formData.addressStreet}
                                                            onChange={handleChange}
                                                            placeholder="Введите улицу"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group">
                                                        <label>Дом:</label>
                                                        <input
                                                            type="text"
                                                            name="addressHome"
                                                            value={formData.addressHome}
                                                            onChange={handleChange}
                                                            placeholder="Введите номер дома"
                                                        />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Квартира:</label>
                                                        <input
                                                            type="text"
                                                            name="addressApartment"
                                                            value={formData.addressApartment}
                                                            onChange={handleChange}
                                                            placeholder="Введите номер квартиры"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Примечания */}
                                            <div className="form-section">
                                                <h4>Примечания</h4>
                                                <div className="form-row">
                                                    <div className="form-group full-width">
                                                        <label>Примечание:</label>
                                                        <textarea
                                                            name="note"
                                                            value={formData.note}
                                                            onChange={handleChange}
                                                            placeholder="Введите примечание"
                                                            rows="3"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="form-row">
                                                    <div className="form-group full-width">
                                                        <label>Комментарии:</label>
                                                        <textarea
                                                            name="comments"
                                                            value={formData.comments}
                                                            onChange={handleChange}
                                                            placeholder="Введите комментарии"
                                                            rows="3"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                ) : order ? (
                                    <div className="order-details">
                                        <div className="details-grid">
                                            <div className="detail-group">
                                                <h4>Основная информация</h4>
                                                <div className="detail-item">
                                                    <label>Номер счета:</label>
                                                    <span>{order.accountNumber || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Исполнитель:</label>
                                                    <span>{order.executorName || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Тип документа:</label>
                                                    <span>{order.documentType || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Дата документа:</label>
                                                    <span>{formatDateToRussian(order.documentDate)}</span>
                                                </div>
                                            </div>

                                            <div className="detail-group">
                                                <h4>Взысканная судом сумма</h4>                                            
                                                <div className="detail-item">
                                                    <label>Основной долг:</label>
                                                    <span>{formatCurrency(order.amountRecoveredFee)}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Пени:</label>
                                                    <span>{formatCurrency(order.amountRecoveredPenalties)}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Пошлина:</label>
                                                    <span>{formatCurrency(order.amountRecoveredToll)}</span>
                                                </div>
                                            </div>

                                            <div className="detail-group">
                                                <h4>Судебная информация</h4>
                                                <div className="detail-item">
                                                    <label>Суд:</label>
                                                    <span>{order.nameCourt || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Номер дела:</label>
                                                    <span>{order.caseNumber || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Результат:</label>
                                                    <span>{order.reviewResult || '-'}</span>
                                                </div>
                                                <div className="detail-item">
                                                    <label>Дата решения:</label>
                                                    <span>{formatDateToRussian(order.decisionDate)}</span>
                                                </div>
                                            </div>

                                            {order.note && (
                                                <div className="detail-group full-width">
                                                    <h4>Примечание</h4>
                                                    <div className="detail-item">
                                                        <span>{order.note}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}

                        {activeTab === 'details' && (
                            <div className="statements-tab">
                                {detailsMessage && (
                                    <div className={`message ${detailsMessage.includes('Ошибка') ? 'error' : 'success'}`}>
                                        {detailsMessage}
                                    </div>
                                )}
                                {detailsLoading ? (
                                    <div className="loading">Загрузка детализации...</div>
                                ) : orderDetails.length === 0 ? (
                                    <div className="no-data">Нет данных по детализации</div>
                                ) : (
                                    <div className="table-wrapper" style={{ maxHeight: '500px', overflowY: 'auto', overflowX: 'auto' }}>
                                        <table className="statements-table">
                                            <thead>
                                                <tr>
                                                    <th rowSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle', width: '100px' }}>
                                                        Дата
                                                    </th>
                                                    <th rowSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle', width: '80px' }}>
                                                        Номер
                                                    </th>
                                                    <th rowSpan="2" style={{ textAlign: 'center', verticalAlign: 'middle', width: '100px' }}>
                                                        Кредит
                                                    </th>
                                                    <th colSpan="4" style={{ textAlign: 'center', borderBottom: '1px solid #dee2e6', borderLeft: '2px solid #6c757d' }}>
                                                        До операции
                                                    </th>
                                                    <th colSpan="3" style={{ textAlign: 'center', borderBottom: '1px solid #dee2e6', borderLeft: '2px solid #6c757d' }}>
                                                        Расписано
                                                    </th>
                                                    <th colSpan="4" style={{ textAlign: 'center', borderBottom: '1px solid #dee2e6', borderLeft: '2px solid #6c757d' }}>
                                                        После операции
                                                    </th>
                                                </tr>
                                                <tr>
                                                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap', borderLeft: '2px solid #6c757d' }}>Основной</th>
                                                    <th style={{ textAlign: 'right' }}>Пени</th>
                                                    <th style={{ textAlign: 'right' }}>Пошлина</th>
                                                    <th style={{ textAlign: 'right' }}>Итого</th>
                                                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap', borderLeft: '2px solid #6c757d' }}>Основной</th>
                                                    <th style={{ textAlign: 'right' }}>Пени</th>
                                                    <th style={{ textAlign: 'right' }}>Пошлина</th>
                                                    <th style={{ textAlign: 'right', whiteSpace: 'nowrap', borderLeft: '2px solid #6c757d' }}>Основной</th>
                                                    <th style={{ textAlign: 'right' }}>Пени</th>
                                                    <th style={{ textAlign: 'right' }}>Пошлина</th>
                                                    <th style={{ textAlign: 'right' }}>Итого</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {orderDetails.map((item, index) => (
                                                    <tr key={index}>
                                                        <td style={{ textAlign: 'center' }}>{formatDateToRussian(item.date)}</td>
                                                        <td style={{ textAlign: 'center' }}>{item.number || '-'}</td>
                                                        <td className="number-cell">{formatCurrency(item.credit)}</td>
                                                        <td className="number-cell" style={{ borderLeft: '2px solid #6c757d' }}>{formatCurrency(item.feeBefore)}</td>
                                                        <td className="number-cell">{formatCurrency(item.penaltyBefore)}</td>
                                                        <td className="number-cell">{formatCurrency(item.tollBefore)}</td>
                                                        <td className="number-cell">{formatCurrency(item.totalBefore)}</td>
                                                        <td className="number-cell" style={{ borderLeft: '2px solid #6c757d' }}>{formatCurrency(item.feeDivided)}</td>
                                                        <td className="number-cell">{formatCurrency(item.penaltyDivided)}</td>
                                                        <td className="number-cell">{formatCurrency(item.tollDivided)}</td>
                                                        <td className="number-cell" style={{ borderLeft: '2px solid #6c757d' }}>{formatCurrency(item.feeAfter)}</td>
                                                        <td className="number-cell">{formatCurrency(item.penaltyAfter)}</td>
                                                        <td className="number-cell">{formatCurrency(item.tollAfter)}</td>
                                                        <td className="number-cell">{formatCurrency(item.totalAfter)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'statements' && (
                            <div className="statements-tab">
                                <h4>Выписки по приказу</h4>

                                {statementsLoading ? (
                                    <div className="loading">Загрузка выписок...</div>
                                ) : statements.length === 0 ? (
                                    <div className="no-data">Нет выписок по этому приказу</div>
                                ) : (
                                    <div className="table-wrapper">
                                        <table className="statements-table">
                                            <thead>
                                                <tr style={{ height: '50px', minHeight: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Дата
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Номер
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Вид операции
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '200px', minWidth: '200px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Контрагент
                                                    </th>
                                                    {/* <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        ИНН контрагента
                                                    </th> */}
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        БИК банка контрагента
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '100px', minWidth: '100px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Счет контрагента
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Кредит
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '400px', minWidth: '400px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Назначение
                                                    </th>
                                                     <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Примечание
                                                    </th>
                                                    <th rowSpan="2" style={{ width: '50px', minWidth: '50px', textAlign: 'center', verticalAlign: 'middle' }}>
                                                        Действия
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {statements.map(statement => (
                                                    <tr
                                                        key={statement.id}
                                                        style={{ cursor: 'pointer' }}
                                                    >
                                                        <td>{formatDateToRussian(statement.date)}</td>
                                                        <td>{statement.number}</td>
                                                        <td>{statement.typeOperation}</td>
                                                        <td>{statement.counterparty}</td>
                                                        {/* <td>{statement.counterpartyINN}</td> */}
                                                        <td>{statement.counterpartyBIK}</td>
                                                        <td>{statement.counterpartyAccount}</td>
                                                        <td className="number-cell">{formatCurrency(statement.credit)}</td>
                                                        <td>{statement.destination}</td>
                                                        <td>{statement.comment}</td>
                                                        <td style={{ textAlign: 'center' }}>
                                                            {user && (user.role === 'admin' || user.role === 'accountant') && (
                                                                <button
                                                                    onClick={(e) => handleDeleteStatement(e, statement.id)}
                                                                    className="btn-delete"
                                                                    title="Удалить"
                                                                >
                                                                    <span>🗑️</span>
                                                                </button>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderModal;